/* eslint-disable security/detect-unsafe-regex */
import isCidr from 'is-cidr';
import isIp from 'is-ip';

const EMAIL_REGEX =
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
const NAME_REGEX = /^[^`~!@#$%^&*()_+={}[\]|\\:/;“"'’<,>?๐฿]*$/;
const COMPANY_REGEX = /^[^`~#$%^*()+={}[\]|\\:;“"'’<>๐฿]*$/;
const PHONE_REGEX = /^\+\d{7,15}$/; // eg: "+14325551212"

const VALID_COUNTRY_CODES = new Set([
    '1', '7',
    '20', '27', '30', '31', '32', '33', '34', '36', '39',
    '40', '41', '43', '44', '45', '46', '47', '48', '49',
    '51', '52', '53', '54', '55', '56', '57', '58',
    '60', '61', '62', '63', '64', '65', '66',
    '81', '82', '84', '86',
    '90', '91', '92', '93', '94', '95', '98',
    '211', '212', '213', '216', '218',
    '220', '221', '222', '223', '224', '225', '226', '227', '228', '229',
    '230', '231', '232', '233', '234', '235', '236', '237', '238', '239',
    '240', '241', '242', '243', '244', '245', '246', '247', '248', '249',
    '250', '251', '252', '253', '254', '255', '256', '257', '258',
    '260', '261', '262', '263', '264', '265', '266', '267', '268', '269',
    '290', '291', '297', '298', '299',
    '350', '351', '352', '353', '354', '355', '356', '357', '358', '359',
    '370', '371', '372', '373', '374', '375', '376', '377', '378', '379',
    '380', '381', '382', '383', '385', '386', '387', '389',
    '420', '421', '423',
    '500', '501', '502', '503', '504', '505', '506', '507', '508', '509',
    '590', '591', '592', '593', '594', '595', '596', '597', '598', '599',
    '670', '672', '673', '674', '675', '676', '677', '678', '679',
    '680', '681', '682', '683', '685', '686', '687', '688', '689',
    '690', '691', '692',
    '800', '808', '850', '852', '853', '855', '856',
    '870', '878', '880', '881', '882', '883', '886', '888',
    '960', '961', '962', '963', '964', '965', '966', '967', '968',
    '970', '971', '972', '973', '974', '975', '976', '977', '979',
    '992', '993', '994', '995', '996', '998',
]);

function hasValidCountryCode(phone) {
    const digits = phone.replace('+', '');
    return (
        VALID_COUNTRY_CODES.has(digits.slice(0, 3)) ||
        VALID_COUNTRY_CODES.has(digits.slice(0, 2)) ||
        VALID_COUNTRY_CODES.has(digits.slice(0, 1))
    );
}
const IP_RANGE_REGEX = /^([01]?\d\d?|2[0-4]\d|25[0-5])(?:\.(?:[01]?\d\d?|2[0-4]\d|25[0-5])){3}(?:\/[0-2]\d|\/3[0-2])?$/; // eg: "192.128.0.1/24"
const PASSWORD_REGEX = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*])/;
// eslint-disable-next-line
const ASCII_REGEX = /[^\x00-\x7f]/;
const WHITESPACE_REGEX = /(^\s+)|(\s+$)/;
const LOWERCASE_REGEX = /[a-z]/;
const UPPERCASE_REGEX = /[A-Z]/;
const NUMBER_REGEX = /\d/;
const SPECIAL_CHAR_REGEX = /[!@#$%^&*]/;

const PASSWORD_MIN_LENGTH = 13;
const PASSWORD_MAX_LENGTH = 255;

class Validator {
    isEmail(email) {
        return EMAIL_REGEX.test(email);
    }

    isValidName(name) {
        return NAME_REGEX.test(name);
    }

    checkName(name) {
        let errors = [];
        if (!this.isValidName(name)) {
            return [...errors, 'Shouldn\'t include special character'];
        }
        if (name.length > 50) {
            return [...errors, 'Maximum 50 characters allowed'];
        }

        return errors;
    }

    checkReferralSourceDetail(value) {
        let errors = [];
        if (!this.isValidName(value)) {
            return [...errors, 'Shouldn\'t include special character'];
        }
        if (value.length > 500) {
            return [...errors, 'Maximum 100 characters allowed'];
        }

        return errors;
    }

    isValidCompany(name) {
        return COMPANY_REGEX.test(name);
    }

    checkCompany(name) {
        let errors = [];
        if (!this.isValidCompany(name)) {
            return [...errors, 'Contains disallowed characters'];
        }
        if (name.length > 256) {
            return [...errors, 'Maximum 256 characters allowed'];
        }

        return errors;
    }

    checkCountry(country) {
        return !country ? ['Country cannot be blank'] : [];
    }

    makeUpPhoneNumber(number) {
        return number.replace(/['"\-_.\s]/g, '');
    }

    isValidPhoneNumber(number) {
        number = number.replace(/['"\-_.\s]/g, '');
        return PHONE_REGEX.test(number) && hasValidCountryCode(number);
    }

    getPasswordValidation(password) {
        return {
            hasMinLength: password.length >= PASSWORD_MIN_LENGTH,
            hasMaxLength: password.length <= PASSWORD_MAX_LENGTH,
            hasLowercase: LOWERCASE_REGEX.test(password),
            hasUppercase: UPPERCASE_REGEX.test(password),
            hasNumber: NUMBER_REGEX.test(password),
            hasSpecialChar: SPECIAL_CHAR_REGEX.test(password),
            hasNoNonAscii: !ASCII_REGEX.test(password),
            hasNoLeadingOrTrailingSpaces: !WHITESPACE_REGEX.test(password),
            validPassword: PASSWORD_REGEX.test(password)
        };
    }

    checkPassword(password) {
        const validation = this.getPasswordValidation(password);

        // prioritize basic rules
        if (!validation.hasNoNonAscii) {
            return ['Disallow non-ASCII characters'];
        } else if (!validation.hasNoLeadingOrTrailingSpaces) {
            return ['Password cannot contain leading or trailing spaces'];
        }

        // password strength rules
        if (!validation.hasMinLength) {
            return ['Minimum password length is 13'];
        } else if (!validation.hasMaxLength) {
            return ['Maximum password length is 255'];
        } else if (!validation.validPassword) {
            return [
                'The password must contain at least one lowercase letter, one uppercase letter, one number and one special character'
            ];
        }

        return [];
    }

    isValidTotp(totp) {
        return totp && /^\d{6}$/.test(totp);
    }

    isValidRecoveryCode(code) {
        return code && /^\w{5}-\w{5}$/.test(code);
    }

    isIpRange(ip) {
        // Input is IPv4/IPv6 or CIDR
        return ip && (isIp(ip) || isCidr(ip) || IP_RANGE_REGEX.test(ip));
    }
}

export default new Validator();
